/**
 * Atalhos de venda assistida (BPe): envio direto ao canal do cliente em 1 clique.
 *
 * Reaproveita o caminho de envio existente (`useMessages` -> POST /messages), que já
 * sobe arquivos ao S3 e resolve canal/telefone no servidor. Expõe feedback por ação
 * (enviando/enviado/erro) e faz fallback de mídia -> link quando o canal é só-texto.
 */

import { useCallback, useMemo, useState } from "react";
import { useMessages } from "../../hooks/useMessages";
import { uploadOne } from "../../hooks/useS3Upload";
import { isValidPhoneField, phoneDigitsOnly } from "../../utils/phone";

/**
 * Classifica a capacidade do canal a partir do `channelType` (string normalizada na UI).
 * @returns {{ canText: boolean, canMedia: boolean, kind: string }}
 */
export function resolveBpeChannelCapability(channelType) {
  const s = String(channelType || "").toLowerCase();
  if (!s) return { canText: false, canMedia: false, kind: "unknown" };
  if (s.includes("phone") || s.includes("voz") || s.includes("voice") || s.includes("call") || s.includes("telefone")) {
    return { canText: false, canMedia: false, kind: "phone" };
  }
  if (s.includes("whatsapp") || s.includes("wpp") || s.includes("apifacil")) {
    return { canText: true, canMedia: true, kind: "whatsapp" };
  }
  if (s.includes("messenger") || s.includes("instagram") || s.includes("direct")) {
    return { canText: true, canMedia: true, kind: "social" };
  }
  if (s.includes("email") || s.includes("mail")) {
    return { canText: true, canMedia: true, kind: "email" };
  }
  if (s.includes("sms") || s.includes("rcs")) {
    return { canText: true, canMedia: false, kind: "sms" };
  }
  // Padrão conservador: assume texto.
  return { canText: true, canMedia: false, kind: "other" };
}

const SENT_RESET_MS = 2500;

function hasValidOutboundPhone(phone) {
  const raw = String(phone ?? "").trim();
  if (!raw || raw === "-" || raw === "—") return false;
  if (isValidPhoneField(raw)) return true;
  return phoneDigitsOnly(raw).length >= 10;
}

/**
 * Hook de envio assistido para o wizard de BPe.
 * @param {{ conversationId:number, channelId:number, channelType:string, projectId:number, phone:string }} info
 */
export function useBpeAssistedSend({ conversationId, channelId, channelType, projectId, phone } = {}) {
  const { sendMessageMutation } = useMessages();
  const capability = useMemo(() => resolveBpeChannelCapability(channelType), [channelType]);
  const [statuses, setStatuses] = useState({});

  const setStatus = useCallback((key, status) => {
    setStatuses((prev) => ({ ...prev, [key]: status }));
  }, []);

  const canSend = Boolean(
    conversationId &&
      channelId &&
      capability.canText &&
      (capability.kind !== "whatsapp" || hasValidOutboundPhone(phone))
  );

  const run = useCallback(
    async (key, payload) => {
      if (!canSend) return false;
      setStatus(key, "sending");
      try {
        await sendMessageMutation.mutateAsync({
          conversationId: Number(conversationId),
          channel: channelId,
          projectId,
          phone: phone || "",
          ...payload,
        });
        setStatus(key, "sent");
        setTimeout(() => {
          setStatuses((prev) => (prev[key] === "sent" ? { ...prev, [key]: "idle" } : prev));
        }, SENT_RESET_MS);
        return true;
      } catch (e) {
        setStatus(key, "error");
        return false;
      }
    },
    [canSend, conversationId, channelId, projectId, phone, sendMessageMutation, setStatus]
  );

  const sendText = useCallback((key, text) => run(key, { message: String(text || "") }), [run]);

  const sendMedia = useCallback(
    async (key, file, caption) => {
      if (!canSend || !file) return false;
      if (capability.canMedia) {
        return run(key, { message: caption || "", file });
      }
      // Canal só-texto: sobe a mídia e envia o link no corpo.
      setStatus(key, "sending");
      try {
        const up = await uploadOne(file, { prefix: "chat" });
        const link = up?.url || up?.fileUrl;
        if (!link) throw new Error("upload_failed");
        return run(key, { message: `${caption ? `${caption}\n` : ""}${link}` });
      } catch (e) {
        setStatus(key, "error");
        return false;
      }
    },
    [canSend, capability.canMedia, run, setStatus]
  );

  return {
    capability,
    canSend,
    statuses,
    statusOf: (key) => statuses[key] || "idle",
    sendText,
    sendImage: sendMedia,
    sendDocument: sendMedia,
  };
}
